import { useState, useCallback } from 'react';
import {
  getDocumentacionByTipoCobertura,
  getTiposCoberturaReintegro,
} from '../../../services/Busquedas/TiposCoberturas';
import {
  getComprobanteAutorizacion,
  getProvidenciaAutorizacion,
} from '../../../services/Afiliado/Autorizaciones';
import {
  getReintegroService,
  getReintegrosService,
  saveReintegroService,
} from '../helpers/reintegroService';

export const useReintegro = () => {
  const [reintegros, setReintegros] = useState([]);
  const [reintegro, setReintegro] = useState(null);
  const [documentacionList, setDocumentacionList] = useState([]);
  const [tiposCobertura, setTiposCobertura] = useState(null);
  const [tiposDocumentacion, setTiposDocumentacion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getReintegros = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getReintegrosService();
      setReintegros(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getReintegro = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    // si no viene valor en id, no hago nada
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      const data = await getReintegroService(id);
      setReintegro(data);
      if (data?.documentacion) {
        await getDocumentacionList(data.documentacion);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveReintegro = useCallback(async (reintegro) => {
    setLoading(true);
    setError(null);
    try {
      const response = await saveReintegroService(reintegro);
      setReintegro(response);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getDocumentacionList = useCallback(async (documentacion = []) => {
    setLoading(true);
    setError(null);
    try {
      if (!Array.isArray(documentacion)) return setDocumentacionList([]);
      const grupos = documentacion.reduce((acc, doc) => {
        const codigo = doc?.codigoDocumentacion;
        const key = codigo ? String(codigo) : 'sinCodigo'; // agrupa sin código bajo 'sinCodigo'

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(doc);
        return acc;
      }, {});

      const result = Object.keys(grupos)
        .map((codigo) => ({ codigo, documentos: grupos[codigo] }))
        .sort((a, b) => parseInt(a.codigo) - parseInt(b.codigo));

      setDocumentacionList(result);
    } catch (err) {
      setError(err);
      setDocumentacionList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const getComprobanteReintegro = useCallback(async (idSolicitud) => {
    setLoading(true);
    setError(null);
    try {
      const urlReporte = await getComprobanteAutorizacion({
        id: idSolicitud,
        enviarMail: false,
      });
      return urlReporte;
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getProvidenciaReintegro = useCallback(async (idSolicitud) => {
    setLoading(true);
    setError(null);
    try {
      const urlProvidencia = await getProvidenciaAutorizacion({
        id: idSolicitud,
      });
      return urlProvidencia;
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getTiposDocumentacionByCobertura = useCallback(async (idTipoCobertura) => {
    setLoading(true);
    setError(null);

    if (!idTipoCobertura) {
      setTiposDocumentacion([]);
      setLoading(false);
      return;
    }

    try {
      const response = await getDocumentacionByTipoCobertura(idTipoCobertura);

      if (response) {
        const modificarNombrePropiedades = response.map((item) => ({
          ...item,
          descripcion: item.documentacion,
          codigo: item.codigoDocumentacion,
        }));
        setTiposDocumentacion(modificarNombrePropiedades);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    reintegro,
    reintegros,
    documentacionList,
    error,
    getComprobanteReintegro,
    getReintegro,
    getReintegros,
    getProvidenciaReintegro,
    getTiposDocumentacionByCobertura,
    loading,
    saveReintegro,
    setReintegro,
    tiposCobertura,
    tiposDocumentacion,
  };
};
